import React from "react"

import { ChevronDown } from "lucide-react"

import { JobPostingSegment, JobPostingStage } from "@/types/jobPosting"

// Button is used in the original DraggableStagesList but not needed in this simplified version
import { Badge } from "../ui/badge"
import { Checkbox } from "../ui/checkbox"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible"
import { Label } from "../ui/label"
import { Switch } from "../ui/switch"
import ContractEditor from "./ContractEditor"

// Define segment type
interface Segment {
  id: string
  label: string
  required?: boolean
  ageLimit?: number
}

// Define the stage type
export interface Stage {
  id: string
  label: string
  required: boolean
  icon?: React.ElementType
  color?: string
  segments: Segment[]
  meta?: Record<string, string | number | string[] | number[] | boolean> // Dynamic meta fields
}

interface StageItemProps {
  stage: Stage
  isSelected: boolean
  onToggle: (stageId: string, isSelected: boolean) => void
  selectedStages: JobPostingStage[]
  onSegmentChange: (
    stageId: string,
    segmentId: string,
    changes: {
      isSelected?: boolean
      meta?: Record<string, string | number | string[] | number[] | boolean>
    },
  ) => void
  onMetaChange: (
    stageId: string,
    key: string,
    value: string | number | string[] | number[] | boolean,
  ) => void
}

// Component for a single stage item
const StageItem: React.FC<StageItemProps> = ({
  stage,
  isSelected,
  onToggle,
  selectedStages,
  onSegmentChange,
  onMetaChange,
}) => {
  const isExpandable =
    (stage.meta && Object.keys(stage.meta).length > 0) ||
    (stage.segments && stage.segments.length > 0)
  return (
    <div>
      <Collapsible className="bg-white border rounded-md shadow-sm overflow-hidden mb-2">
        {/* Header - Always visible */}
        <div className="flex items-center space-x-3 p-3">
          {/* Right side with toggle and badges */}
          <div className="flex items-center gap-3">
            {/* Smaller switch */}
            <Switch
              id={`stage-${stage.id}`}
              checked={isSelected}
              onCheckedChange={(checked) => onToggle(stage.id, checked)}
              disabled={stage.required}
            />
          </div>
          {isExpandable ? (
            <CollapsibleTrigger className="flex flex-1 items-center gap-2">
              {/* Icon on the left */}
              {stage.icon && (
                <div style={{ color: stage.color || "currentColor" }} className="flex-shrink-0">
                  {React.createElement(stage.icon, { size: 20 })}
                </div>
              )}

              {/* Stage title */}
              <span className="font-semibold cursor-pointer text-left">{stage.label}</span>
            </CollapsibleTrigger>
          ) : (
            <div className="flex flex-1 items-center gap-2">
              {/* Icon on the left */}
              {stage.icon && (
                <div style={{ color: stage.color || "currentColor" }} className="flex-shrink-0">
                  {React.createElement(stage.icon, { size: 20 })}
                </div>
              )}

              {/* Stage title */}
              <span className="font-semibold text-left">{stage.label}</span>
            </div>
          )}
          {/* Badges and expand trigger */}
          <div className="flex items-center gap-2">
            {stage.required && (
              <Badge variant="default" className="p-1 px-2 text-xs pointer-events-none">
                Required
              </Badge>
            )}
            {isSelected && isExpandable && (
              <CollapsibleTrigger className="ml-2">
                <ChevronDown className="h-4 w-4 transition-transform duration-200 collapsible-chevron" />
              </CollapsibleTrigger>
            )}
          </div>
        </div>

        {/* Expanded content with sliding animation - only shown if stage is selected */}
        {isSelected && isExpandable && (
          <CollapsibleContent className="CollapsibleContent">
            <div className="p-3 pt-2 border-t text-sm text-palette-jungleGreen600">
              {/* Meta fields if applicable */}
              {stage.meta && Object.keys(stage.meta).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Stage Properties:</h4>
                  {/* Special handling for Contract stage: rich editor + defaultSalary */}
                  {stage.id === "contract" ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor={`meta-${stage.id}-defaultSalary`}
                          className="font-medium w-32"
                        >
                          Default Salary:
                        </label>
                        <input
                          id={`meta-${stage.id}-defaultSalary`}
                          type="number"
                          className="w-28 h-8 text-sm border rounded px-2"
                          defaultValue={
                            (selectedStages.find((s) => s.id === stage.id)?.meta?.[
                              "defaultSalary"
                            ] as number | undefined) ??
                            (stage.meta?.defaultSalary as number | undefined) ??
                            0
                          }
                          min={0}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10)
                            if (!isNaN(value)) onMetaChange(stage.id, "defaultSalary", value)
                          }}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="font-medium">Contract:</label>
                        <ContractEditor
                          content={
                            (selectedStages.find((s) => s.id === stage.id)?.meta?.["contract"] as
                              string | undefined) ??
                            (stage.meta?.contract as string | undefined) ??
                            ""
                          }
                          onChange={(newContent) => onMetaChange(stage.id, "contract", newContent)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(stage.meta).map(([key, value]) => {
                        const inputType = typeof value === "number" ? "number" : "text"
                        const min = inputType === "number" ? 0 : undefined
                        const max = key === "passMark" ? 100 : undefined

                        return (
                          <div key={key} className="flex items-center gap-2">
                            <label
                              htmlFor={`meta-${stage.id}-${key}`}
                              className="font-medium w-24 capitalize"
                            >
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                              :
                            </label>
                            <input
                              id={`meta-${stage.id}-${key}`}
                              type={inputType}
                              className="w-20 h-8 text-sm border rounded px-2"
                              defaultValue={
                                (selectedStages.find((s) => s.id === stage.id)?.meta?.[key] as
                                  string | number | undefined) ?? (value as string | number)
                              }
                              min={min}
                              max={max}
                              onChange={(e) => {
                                let newValue: string | number = e.target.value
                                if (inputType === "number") {
                                  newValue = parseInt(e.target.value, 10)
                                  if (isNaN(newValue)) return
                                }
                                onMetaChange(stage.id, key, newValue)
                              }}
                            />
                            {key === "passMark" && (
                              <span className="text-xs text-palette-jungleGreen500">(0-100)</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Segments with checkboxes */}
              {stage.segments && stage.segments.length > 0 && (
                <div>
                  <div className="space-y-2">
                    {stage.segments.map((segment) => {
                      const selectedStage = selectedStages.find((s) => s.id === stage.id)
                      const selectedSeg = selectedStage?.segments?.find((s) => s.id === segment.id)
                      const isSegSelected = segment.required || !!selectedSeg

                      return (
                        <div key={segment.id} className="flex flex-col gap-1">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`segment-${stage.id}-${segment.id}`}
                              checked={isSegSelected}
                              onCheckedChange={(checked) =>
                                onSegmentChange(stage.id, segment.id, {
                                  isSelected: checked === true,
                                })
                              }
                              disabled={segment.required}
                            />
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <label
                                  htmlFor={`segment-${stage.id}-${segment.id}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {segment.label}
                                </label>
                                {segment.id === "dob" && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-palette-jungleGreen500">
                                      Age limit:
                                    </span>
                                    <input
                                      type="number"
                                      className="w-14 h-6 text-xs border rounded px-2"
                                      defaultValue={
                                        (selectedSeg?.meta?.ageLimit as number | undefined) ??
                                        segment.ageLimit ??
                                        18
                                      }
                                      min="0"
                                      max="100"
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value, 10)
                                        if (!isNaN(value)) {
                                          onSegmentChange(stage.id, segment.id, {
                                            meta: { ageLimit: value },
                                          })
                                        }
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              {segment.required && (
                                <Badge variant="outline" className="text-xs ml-2">
                                  Required
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Tax type options for personal-details > tax-details (multi-select) */}
                          {segment.id === "tax-details" && isSegSelected && (
                            <div className="pl-6 pt-1">
                              {(() => {
                                const options = [
                                  {
                                    value: "sole-trader",
                                    label: "Sole Trader",
                                  },
                                  {
                                    value: "limited-company",
                                    label: "Limited Company",
                                  },
                                  {
                                    value: "partnership",
                                    label: "Partnership",
                                  },
                                  {
                                    value: "not-applicable",
                                    label: "Not Applicable",
                                  },
                                ]
                                const metaObj =
                                  (selectedSeg?.meta as Record<string, unknown> | undefined) ??
                                  undefined
                                const maybeTaxTypes = metaObj
                                  ? (metaObj["taxTypes"] as unknown)
                                  : undefined
                                const selectedArr: string[] = Array.isArray(maybeTaxTypes)
                                  ? (maybeTaxTypes as string[])
                                  : (() => {
                                      const maybeTaxType = metaObj
                                        ? (metaObj["taxType"] as unknown)
                                        : undefined
                                      return typeof maybeTaxType === "string" &&
                                        maybeTaxType.length > 0
                                        ? [maybeTaxType]
                                        : []
                                    })()
                                const handleToggle = (
                                  taxValue: string,
                                  checked: boolean | "indeterminate",
                                ) => {
                                  const prev = selectedArr
                                  const next = (checked as boolean)
                                    ? Array.from(new Set([...prev, taxValue]))
                                    : prev.filter((v) => v !== taxValue)
                                  onSegmentChange(stage.id, segment.id, {
                                    meta: { taxTypes: next },
                                  })
                                }
                                return (
                                  <div className="flex flex-col gap-2">
                                    {options.map((opt) => {
                                      const id = `${opt.value}-${stage.id}`
                                      const isChecked = selectedArr.includes(opt.value)
                                      return (
                                        <div
                                          className="flex items-center space-x-2"
                                          key={opt.value}
                                        >
                                          <Checkbox
                                            id={id}
                                            checked={isChecked}
                                            onCheckedChange={(checked) =>
                                              handleToggle(opt.value, checked)
                                            }
                                          />
                                          <Label htmlFor={id}>{opt.label}</Label>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )
                              })()}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>
    </div>
  )
}

interface StagesListProps {
  stages: Stage[]
  selectedStages: JobPostingStage[]
  setSelectedStages: (stages: JobPostingStage[]) => void
}

const StagesList: React.FC<StagesListProps> = ({ stages, selectedStages, setSelectedStages }) => {
  // Check if a stage is selected
  const isStageSelected = (stageId: string) => {
    return selectedStages.some((stage) => stage.id === stageId)
  }

  // Handle toggling a stage on/off
  const handleToggleStage = (stageId: string, isSelected: boolean) => {
    if (isSelected) {
      // Add the stage if it doesn't exist
      if (!selectedStages.find((stage) => stage.id === stageId)) {
        // Find the stage configuration
        const stageConfig = stages.find((stage) => stage.id === stageId)

        if (stageConfig) {
          // Initialize with required segments pre-selected
          const requiredSegments = stageConfig.segments
            .filter((segment) => segment.required)
            .map((segment) => {
              // Special handling for DOB segment to include ageLimit
              if (segment.id === "dob") {
                return {
                  id: segment.id,
                  meta: { ageLimit: segment.ageLimit || 18 },
                }
              }
              return { id: segment.id }
            })

          // Create the new stage with segments
          const newStage: JobPostingStage = {
            id: stageId,
            segments: requiredSegments,
          }

          // If the stage has meta fields in the config, add them to the selected stage
          if (stageConfig.meta && Object.keys(stageConfig.meta).length > 0) {
            newStage.meta = { ...stageConfig.meta }
          }

          // Insert by config order instead of appending
          const orderMap = new Map(stages.map((s, i) => [s.id, i] as const))
          const newStages = [...selectedStages, newStage].sort((a, b) => {
            const ai = orderMap.get(a.id) ?? 0
            const bi = orderMap.get(b.id) ?? 0
            return ai - bi
          })
          setSelectedStages(newStages)
        }
      }
    } else {
      // Remove the stage if it exists and is not required
      const stageConfig = stages.find((stage) => stage.id === stageId)
      if (stageConfig && !stageConfig.required) {
        const newStages = selectedStages.filter((stage) => stage.id !== stageId)
        setSelectedStages(newStages)
      }
    }
  }

  // Handle segment changes (toggle or update fields)
  const handleSegmentChange = (
    stageId: string,
    segmentId: string,
    changes: { isSelected?: boolean; meta?: Record<string, string | number> },
  ) => {
    // Ensure the stage exists in state (required stages may render as selected even if missing from state)
    let stagesWorking = selectedStages
    if (!selectedStages.some((s) => s.id === stageId)) {
      const stageConfig = stages.find((s) => s.id === stageId)
      if (stageConfig) {
        const requiredSegments: JobPostingSegment[] = (
          stageConfig.segments ? stageConfig.segments.filter((seg) => seg.required) : []
        ).map((seg) =>
          seg.id === "dob"
            ? { id: seg.id, meta: { ageLimit: seg.ageLimit || 18 } }
            : { id: seg.id },
        )

        const newStage: JobPostingStage = {
          id: stageId,
          segments: requiredSegments,
        }

        if (stageConfig.meta && Object.keys(stageConfig.meta).length > 0) {
          newStage.meta = { ...stageConfig.meta }
        }

        // Insert by config order instead of appending
        const orderMap = new Map(stages.map((s, i) => [s.id, i] as const))
        stagesWorking = [...selectedStages, newStage].sort((a, b) => {
          const ai = orderMap.get(a.id) ?? 0
          const bi = orderMap.get(b.id) ?? 0
          return ai - bi
        })
      }
    }

    // Build segment order map from config to keep segments ordered as in config
    const stageConfigForOrder = stages.find((s) => s.id === stageId)
    const segmentOrderMap = new Map(
      (stageConfigForOrder?.segments ?? []).map((seg, i) => [seg.id, i] as const),
    )

    // Create a new array of stages with the updated segment
    const updatedStages = stagesWorking.map((stage) => {
      if (stage.id === stageId) {
        // Create a new segments array or use existing one
        let nextSegments: JobPostingSegment[] = stage.segments ? [...stage.segments] : []

        // Handle segment selection/deselection
        if (changes.isSelected !== undefined) {
          if (changes.isSelected) {
            // Add segment if not already included
            if (!nextSegments.find((s) => s.id === segmentId)) {
              nextSegments.push({ id: segmentId })
            }
          } else {
            // Remove segment
            nextSegments = nextSegments.filter((s) => s.id !== segmentId)
          }
        }

        // Handle meta field updates (e.g., ageLimit, taxType)
        if (changes.meta !== undefined) {
          const segmentIndex = nextSegments.findIndex((s) => s.id === segmentId)
          if (segmentIndex >= 0) {
            const existingMeta = nextSegments[segmentIndex].meta ?? {}
            nextSegments[segmentIndex] = {
              ...nextSegments[segmentIndex],
              meta: { ...existingMeta, ...changes.meta },
            }
          } else {
            const newSeg: JobPostingSegment = {
              id: segmentId,
              meta: { ...changes.meta },
            }
            nextSegments.push(newSeg)
          }
        }

        // Enforce config order for segments
        if (segmentOrderMap.size > 0) {
          nextSegments = [...nextSegments].sort((a, b) => {
            const ai = segmentOrderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER
            const bi = segmentOrderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER
            return ai - bi
          })
        }

        return { ...stage, segments: nextSegments }
      }
      return stage
    })

    // Update the state with the new array
    setSelectedStages(updatedStages)
  }

  // Handle meta field changes
  const handleMetaChange = (
    stageId: string,
    key: string,
    value: string | number | string[] | number[] | boolean,
  ) => {
    // Create a new array of stages with the updated meta field
    const updatedStages = selectedStages.map((stage) => {
      if (stage.id === stageId) {
        // Create or update the meta object
        const meta = { ...(stage.meta || {}) }
        meta[key] = value

        return {
          ...stage,
          meta,
        }
      }
      return stage
    })

    // Update the state with the new array
    setSelectedStages(updatedStages)
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Job Application Stages</h3>
      <p className="text-sm text-palette-jungleGreen600">
        Select the stages that will be part of this job application process. Required stages cannot
        be removed.
      </p>

      <div className="space-y-2">
        {stages.map((stage) => (
          <StageItem
            key={stage.id}
            stage={stage}
            isSelected={isStageSelected(stage.id) || stage.required}
            onToggle={handleToggleStage}
            selectedStages={selectedStages}
            onSegmentChange={handleSegmentChange}
            onMetaChange={handleMetaChange}
          />
        ))}
      </div>
    </div>
  )
}

export default StagesList
